/**
 * Contains all the times that are not concidered active (red). These are alle passes that are connected to a contestant,
 * but are not the ones that are connected to an actual result list
 */
import { ContestantTime } from '@/objects/generated';
import { defineStore } from 'pinia';
import { useEventStore, useEventStoreRef } from './eventStore';
import { TimeDao } from '@/dao';
import { onUnmounted, watch } from 'vue';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { addToMapWithArray, addToMapWithSet, arrayUnique } from '@/utilities/arrayUtils';
import { compareDateTimes } from '@/utilities/dateutils';
import type { ContestantId, StationId } from '@/utilities/typealiases';
import { useStoredSettings } from '@/composeables/settings';
import { EventJsonSettings } from '@/composeables/settings/EventJsonSettings';
import { useUserStore } from './userStore';
import { ProfileHelper } from '@/utilities/profileHelper';

type ContestantTimeSubscriptionOptions = {
    triggerForExistingTimes?: boolean;
};

type ContestantTimeKey = string;

/**
 * The type the loader has as value
 */
type LoaderValueType = {
    /**
     * Map of all ContestantTimes
     *
     * This is NOT sorted by time, but it is initally loaded sorted correctly, so its semi sorted by time ascending, but you cant trust it
     *
     * Id (EventId-TUID) -> Time
     */
    times: Map<ContestantTimeKey, ContestantTime>;

    /**
     * Map of all contestant times belonging to a contestant
     *
     * This is sorted ascending by time
     *
     * ContestantID -> [Time]
     */
    contestantTimesMap: Map<ContestantId, ContestantTime[]>;

    /**
     * Map of station times
     *
     * StationID -> [Time]
     */
    stationTimeMap: Map<StationId, ContestantTime[]>;
};
export type ContestantTimeStore = ReturnType<typeof useContestantTimeStore>;

export const useContestantTimeStore = defineStore('contestanttimestore', () => {
    // We need to listen to changes to the event
    const { eventID, initpromise } = useEventStoreRef();

    /**
     * List of all the active subscriptions for the times
     */
    const registeredSubscriptions = new Set<Function>();

    /**
     * Keeps track of which TUIDs we already have for each contestant, so we dont duplicate
     */
    const contestantTimesTuidsMap = new Map<ContestantId, Set<string>>();

    /**
     * A loader for the times
     */
    const timeLoader = new LoaderWithReset<LoaderValueType>({
        defaultValue: {
            times: new Map<ContestantTimeKey, ContestantTime>(),
            contestantTimesMap: new Map<ContestantId, ContestantTime[]>(),
            stationTimeMap: new Map<StationId, ContestantTime[]>(),
        },
        loaderFunction: async () => {
            const user = useUserStore().user;
            if (!user){
                return {
                    times: new Map<ContestantTimeKey, ContestantTime>(),
                    contestantTimesMap: new Map<ContestantId, ContestantTime[]>(),
                    stationTimeMap: new Map<StationId, ContestantTime[]>(),
                };
            }
            const eventJsonSettings = await useStoredSettings().GetSettings<EventJsonSettings>(EventJsonSettings, useEventStore().eventID, user.ID);
            if (eventJsonSettings?.DisableContestantTimes ?? false){
                return {
                    times: new Map<ContestantTimeKey, ContestantTime>(),
                    contestantTimesMap: new Map<ContestantId, ContestantTime[]>(),
                    stationTimeMap: new Map<StationId, ContestantTime[]>(),
                };
            }
            /**
             * Note, the times here are sorted ascending by the backend, so these are sorted
             * This will load ALL times for the event, both active and inactive ones
             */
            const times = await TimeDao.GetContestantTimes(eventID.value);

            const value = {
                times: new Map<ContestantTimeKey, ContestantTime>(),
                contestantTimesMap: new Map<ContestantId, ContestantTime[]>(),
                stationTimeMap: new Map<StationId, ContestantTime[]>(),
            };
            addTimesToLoaderValue(value, times);

            return value;
        },
        waitOnBeforeLoad: initpromise.value,
    });

    const addTimesToLoaderValue = (loaderValue: LoaderValueType, times: ContestantTime[]) => {
        const prof = ProfileHelper.start('ContestantTimeStore addTimesToLoaderValue');

        times.forEach((t) => {
            // Add (or update)
            loaderValue.times.set(t.Id!, t);
            if (t.ContestantID) {
                // TODO: What if we overwrite the time. Is that even possible? In the times collection the comment says (or update), which suggests that we can update times
                // Maybe we should have an inner map of timeId -> time
                if (contestantTimesTuidsMap.get(t.ContestantID)?.has(t.TUID)) {
                    // Skip if we get a tuid we already have
                    return;
                }
                addToMapWithArray(loaderValue.contestantTimesMap, t.ContestantID, t);
                addToMapWithSet(contestantTimesTuidsMap, t.ContestantID, t.TUID!);

                if (t.StationID && t.TUID) {
                    addToMapWithArray(loaderValue.stationTimeMap, t.StationID!, t);
                }
            }
        });

        prof.mark('addToLoader-mapsUpdated');

        // Sort the times for each contestant so the map has sorted arrays
        arrayUnique(times.flatMap((t) => t.ContestantID)).forEach((cid) => {
            if (!cid) {
                return;
            }
            const contestantTimes = loaderValue.contestantTimesMap.get(cid);
            if (contestantTimes) {
                contestantTimes.sort((a, b) => {
                    const dateSort = compareDateTimes(a.ClockTime, b.ClockTime);
                    if (dateSort !== 0) {
                        return dateSort;
                    }
                    return a.Id!.localeCompare(b.Id!);
                });
            }
        });

        prof.mark('addToLoader-done');
    };

    /**
     * Reset the loaders
     */
    const reset = () => {
        timeLoader.Reset();
    };

    /**
     * This is a waitable function that makes sure we have loaded all the data we need
     */
    const ensureLoaded = async () => {
        await timeLoader.Load();
    };

    /**
     * Handle the Active times we get on the signalr subscription
     * @param times The times received from the signalr subscription
     */
    const timeReceived = async (times: ContestantTime[]) => {
        const prof = ProfileHelper.start('ContestantTimeStore timeReceived');
        // Make sure we load the active times from the server before we handle our first signalr time.
        // If we dont do this, the load will overwrite the time we just got when its loaded
        await ensureLoaded();

        prof.mark('ensureLoaded');

        // Sort the times we just received so they are ordered ascending
        times.sort((a, b) => {
            const dateSort = compareDateTimes(a.ClockTime, b.ClockTime);
            if (dateSort !== 0) {
                return dateSort;
            }
            return a.Id!.localeCompare(b.Id!);
        });

        prof.mark('sorted');

        addTimesToLoaderValue(timeLoader.Value, times);

        prof.mark('added');

        // Loop through all the subscriptions and call them with the new times
        let subscriptionIndex = 0;
        for (const subscriptionCallback of registeredSubscriptions) {
            // Notify the subscription of all received times of the types they have subscribed to.
            // Time includes all statuses that are not Deleted, eg. DNF, Lap, etc.
            prof.mark(`cb${subscriptionIndex}`);
            subscriptionCallback(times);
            prof.mark(`cb${subscriptionIndex}-done`);
            subscriptionIndex++;
        }

        prof.end();
    };

    /**
     * The unsubscribe function for the active time subscription
     */
    let signalrUnsubscribeFunction: (() => Promise<void>) | null = null;

    /**
     * This will trigger every time the eventID changes so we set up the loaders again
     */
    watch(
        eventID,
        async (nv, ov) => {
            if (nv == ov || nv == 0) {
                return;
            }

            const user = useUserStore().user;
            if (user){
                const eventJsonSettings = await useStoredSettings().GetSettings<EventJsonSettings>(EventJsonSettings, nv, user.ID);
                if (eventJsonSettings?.DisableContestantTimes ?? false){
                    return;
                }
            }

            reset();

            if (signalrUnsubscribeFunction) {
                await signalrUnsubscribeFunction();
            }

            signalrUnsubscribeFunction = await signalrSubscribe.ContestantTime(
                { EventID: eventID.value },
                async (rt) => await timeReceived(rt),
            );
        },
        { immediate: true },
    );

    /**
     * Subscribe to the active times
     * @param callback The callback to be invoked when we get a new time
     * @param options Options for the subscription
     */
    const subscribeToContestantTimes = (
        callback: (times: ContestantTime[]) => void,
        options: ContestantTimeSubscriptionOptions = { triggerForExistingTimes: true }, // Note: Times with any other status is considered to be a Time
    ) => {
        // Add the callback to the list of subscriptions
        registeredSubscriptions.add(callback);

        const unsubscribe = () => {
            registeredSubscriptions.delete(callback);
        };

        // If triggerForExistingActiveTimes is true we need to trigger the callback with the existing active times
        if (options.triggerForExistingTimes) {
            ensureLoaded().then(() => {
                callback(Array.from(timeLoader.Value.times.values()));
            });
        }

        return unsubscribe;
    };

    return {
        /**
         * Ensure that  all the data will be loaded from the server
         * this is a waitable function
         */
        EnsureLoaded: ensureLoaded,

        /**
         * Add a subscription to the active times
         */
        SubscribeToContestantTimes: subscribeToContestantTimes,

        /**
         * Get all contestant ids that has times
         * @returns All contestant ids that has times
         */
        GetContestantIdsWithTimes: () => Array.from(timeLoader.Value.contestantTimesMap.keys()),

        /**
         * Get all times for one contestant
         */
        GetTimesForContestant: (contestantId: number) => timeLoader.Value.contestantTimesMap.get(contestantId) ?? [],

        /**
         * Returns all loaded times
         * @returns
         */
        GetAllContestantTimes: () => Array.from(timeLoader.Value.times.values()),
    };
});

// helper composable to subscribe to the contestant times store
export const useContestantTimesSubscription = async (
    callback: (times: ContestantTime[]) => void,
    options?: ContestantTimeSubscriptionOptions,
) => {
    const contestantTimeStore = useContestantTimeStore();
    const cleanup = contestantTimeStore.SubscribeToContestantTimes(callback, options);

    onUnmounted(() => {
        cleanup();
    });
};
